/**
 * Pair line add — upgrades the form to an AJAX add. Publishing
 * `biosphere:cart:change` re-renders the surface; failures fall back to the
 * native POST.
 */
import { sectionUrl } from "biosphere-section-url";

class BiosphereCartPairAdd extends HTMLElement {
  #abort;
  #form;
  #pending = false;

  connectedCallback() {
    this.#form = this.querySelector("form");
    if (!this.#form) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#form.addEventListener("submit", (event) => this.#onSubmit(event), {
      signal,
    });

    // Cart page only: in the drawer applyHtml replaces this element whole.
    if (!this.closest("biosphere-cart-items")) {
      document.addEventListener("biosphere:cart:change", () => this.#resync(), {
        signal,
      });
    }
  }

  /** Reload the form so it never posts a stale variant. */
  async #resync() {
    const sectionId = document.querySelector("biosphere-cart-items")?.dataset
      .sectionId;
    if (!sectionId) return;

    try {
      const response = await fetch(
        // The page's own query string is kept and `sections` added to it. Built
        // from pathname alone, this asked the server for a different page than
        // the one being viewed: /collections/all?view=black-friday re-rendered
        // as /collections/all, and anything else carried in the URL — a filter,
        // a page number, the preview token — went with it.
        sectionUrl(sectionId),
        { cache: "no-store" },
      );
      const data = await response.json();
      const doc = new DOMParser().parseFromString(data[sectionId], "text/html");
      // getAttribute, not .id: <input name="id"> shadows the property.
      const fresh = doc.getElementById(this.#form.getAttribute("id"));
      if (fresh) this.#form.replaceChildren(...fresh.childNodes);
    } catch {}

    this.#pending = false;
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  async #onSubmit(event) {
    event.preventDefault();

    // One add per click; the re-render replaces the button.
    if (this.#pending) return;
    this.#pending = true;
    const submitter = event.submitter;
    if (submitter) submitter.disabled = true;

    const surface =
      this.closest("biosphere-cart-items") ??
      document.querySelector("biosphere-cart-items");
    surface?.setAttribute("aria-busy", "true");

    try {
      // Our own fields only: apps inject properties[] into add forms.
      const fields = new FormData(this.#form);
      const body = new URLSearchParams();
      body.set("id", fields.get("id"));
      body.set("quantity", fields.get("quantity") ?? "1");

      const added = await fetch(this.#form.action, {
        method: "POST",
        body,
        headers: { Accept: "application/json" },
      });
      const payload = await added.json();

      // Platform add errors arrive as a status field, not an HTTP error.
      if (payload.status) throw new Error(payload.message);

      const response = await fetch(`${this.dataset.cartUrl}.json`, {
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      const cart = await response.json();

      document.dispatchEvent(
        new CustomEvent("biosphere:cart:change", {
          detail: { cart, action: "add", item: payload, source: "cart-pair" },
        }),
      );
    } catch {
      surface?.removeAttribute("aria-busy");
      this.#pending = false;
      if (submitter) submitter.disabled = false;
      this.#form.submit();
    }
  }
}

if (!customElements.get("biosphere-cart-pair-add")) {
  customElements.define("biosphere-cart-pair-add", BiosphereCartPairAdd);
}
